import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Determina quale endpoint API usare in base al ruolo utente
 * WEBAPP-021: API separate per ruolo
 */
const getExercisesEndpoint = (userRole) => {
    switch (userRole) {
        case 'gym':
        case 'trainer':
            return '/get_esercizi_gym.php';
        case 'user':
            return '/get_esercizi_user.php';
        case 'standalone':
            return '/get_esercizi_standalone.php';
        case 'admin':
            return '/esercizi.php'; // Admin vede tutto
        default:
            return '/esercizi.php'; // Fallback
    }
};

/**
 * Hook per gestire il caricamento paginato degli esercizi
 * Supporta infinite scroll, ricerca con debounce, filtri e cache locale
 * 
 * @param {Object} options - Opzioni di configurazione
 * @param {number} options.initialLimit - Numero di esercizi per pagina (default: 50)
 * @param {boolean} options.enableInfiniteScroll - Abilita infinite scroll (default: true)
 * @param {number} options.searchDebounceMs - Debounce per ricerca in ms (default: 300)
 * @param {boolean} options.includeMuscles - Include dati muscoli (default: true)
 * @param {boolean} options.legacy - Usa formato legacy (default: false)
 * @returns {Object} - Stato e funzioni per gestire esercizi paginati
 */
export const usePaginatedExercises = ({
    initialLimit = 50,
    enableInfiniteScroll = true,
    searchDebounceMs = 500, // Aumentato a 500ms per dare più tempo all'utente di finire di scrivere
    includeMuscles = true,
    legacy = false
} = {}) => {
    const { getAuthenticatedApi, currentUser } = useAuth();
    const api = getAuthenticatedApi();
    
    // WEBAPP-021: Determina endpoint in base al ruolo
    const userRole = currentUser?.role_name || null;
    const exercisesEndpoint = getExercisesEndpoint(userRole);
    
    // Stato principale
    const [exercises, setExercises] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false); // Inizia a false perché non carichiamo all'inizio
    const [error, setError] = useState(null);
    
    // Stato paginazione
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    
    // Stato filtri
    const [searchTerm, setSearchTerm] = useState('');
    const [muscleGroup, setMuscleGroup] = useState(null);
    
    // Refs per debounce e cache
    const searchTimeoutRef = useRef(null);
    const debouncedSearchRef = useRef('');
    const cacheRef = useRef(new Map()); // Cache per evitare ricaricamenti
    
    /**
     * Genera chiave cache basata su parametri
     */
    const getCacheKey = useCallback((page, search, group, options = {}) => {
        const equipment = options?.equipment || '';
        const primaryMuscle = options?.primaryMuscle || '';
        return `${page}-${search || ''}-${group || ''}-${equipment}-${primaryMuscle}`;
    }, []);
    
    /**
     * Carica esercizi dalla API
     */
    const fetchExercises = useCallback(async (page = 1, search = '', group = null, append = false, options = {}) => {
        try {
            setLoading(true);
            setError(null);
            
            // Controlla cache
            const cacheKey = getCacheKey(page, search, group, options);
            if (cacheRef.current.has(cacheKey) && append) {
                // Se stiamo appendendo e abbiamo già questa pagina in cache, usa cache
                const cached = cacheRef.current.get(cacheKey);
                setExercises(prev => append ? [...prev, ...cached.data] : cached.data);
                setCurrentPage(page);
                setHasMore(cached.pagination.hasMore);
                setTotal(cached.pagination.total);
                setTotalPages(cached.pagination.totalPages);
                setLoading(false);
                return;
            }
            
            // Costruisci parametri API
            const params = {};
            
            // Paginazione solo se richiesta esplicitamente
            if (page > 1) {
                params.page = page;
                params.limit = initialLimit;
            }
            // Se page = 1, non passiamo limit per avere tutti i risultati
            
            // WEBAPP-021: get_esercizi_standalone.php richiede user_id
            if (userRole === 'standalone' && currentUser) {
                params.user_id = currentUser.user_id || currentUser.id;
            }
            
            // Passa search solo se non è vuoto (evita di passare stringa vuota)
            if (search && search.trim() !== '') {
                params.search = search.trim();
            }
            // Non passare search se è vuoto - questo evita problemi con l'API
            
            if (group && group !== 'tutti' && group !== '') {
                params.gruppo_muscolare = group;
            }
            
            // Nuovi filtri
            const equipment = options?.equipment;
            const primaryMuscle = options?.primaryMuscle;
            
            if (equipment && equipment !== '') {
                params.attrezzatura = equipment;
            }
            
            if (primaryMuscle && primaryMuscle !== '') {
                params.primary_muscle = primaryMuscle;
            }
            
            if (includeMuscles) {
                params.include_muscles = true;
            }
            
            if (legacy) {
                params.legacy = true;
            }
            
            // WEBAPP-021: Chiamata API con endpoint corretto per ruolo
            const response = await api.get(exercisesEndpoint, { params });
            
            // Processa risposta
            let exercisesData = [];
            let pagination = null;
            
            if (response.data && Array.isArray(response.data)) {
                // Formato retrocompatibile (array diretto)
                exercisesData = response.data;
                pagination = null;
            } else if (response.data && response.data.data) {
                // Formato con paginazione {data: [...], pagination: {...}}
                exercisesData = response.data.data;
                pagination = response.data.pagination;
            } else if (response.data && response.data.esercizi && Array.isArray(response.data.esercizi)) {
                // Formato standalone legacy {success: true, esercizi: [...]}
                exercisesData = response.data.esercizi;
                pagination = null;
            } else {
                exercisesData = [];
            }
            
            // Aggiorna stato
            if (append) {
                setExercises(prev => [...prev, ...exercisesData]);
            } else {
                setExercises(exercisesData);
            }
            
            if (pagination) {
                setCurrentPage(page);
                setHasMore(pagination.hasMore || false);
                setTotal(pagination.total || 0);
                setTotalPages(pagination.totalPages || 0);
                
                // Salva in cache
                cacheRef.current.set(cacheKey, {
                    data: exercisesData,
                    pagination: pagination
                });
            } else {
                // Senza paginazione (retrocompatibile)
                setHasMore(false);
                setTotal(exercisesData.length);
            }
            
        } catch (err) {
            console.error('Errore nel caricamento degli esercizi:', err);
            setError(err.response?.data?.message || 'Errore nel caricamento degli esercizi');
            if (!append) {
                setExercises([]);
            }
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    }, [api, initialLimit, includeMuscles, legacy, getCacheKey, userRole, currentUser, exercisesEndpoint]);
    
    /**
     * Carica prima pagina (reset)
     */
    const loadInitial = useCallback((search = '', group = null, options = {}) => {
        // Pulisci cache quando cambiano i filtri (equipment o primaryMuscle)
        // Questo assicura che i nuovi filtri vengano sempre applicati
        if (options?.equipment || options?.primaryMuscle) {
            cacheRef.current.clear();
        }
        
        setExercises([]);
        setCurrentPage(1);
        setSearchTerm(search);
        setMuscleGroup(group);
        debouncedSearchRef.current = search;
        fetchExercises(1, search, group, false, options);
    }, [fetchExercises]);
    
    /**
     * Carica prossima pagina (infinite scroll)
     */
    const loadMore = useCallback(() => {
        if (!loading && hasMore) {
            const nextPage = currentPage + 1;
            fetchExercises(nextPage, debouncedSearchRef.current, muscleGroup, true, {});
        }
    }, [loading, hasMore, currentPage, fetchExercises, muscleGroup]);
    
    /**
     * Ricerca con debounce
     */
    const search = useCallback((term, options = {}) => {
        setSearchTerm(term);
        
        // Cancella timeout precedente
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        // Se ci sono filtri ma non ricerca, carica immediatamente senza debounce
        const hasFilters = options?.equipment || options?.primaryMuscle;
        if (!term && hasFilters) {
            debouncedSearchRef.current = term;
            loadInitial(term, muscleGroup, options);
            return;
        }
        
        // Imposta nuovo timeout per ricerca testuale
        searchTimeoutRef.current = setTimeout(() => {
            debouncedSearchRef.current = term;
            loadInitial(term, muscleGroup, options);
        }, searchDebounceMs);
    }, [loadInitial, muscleGroup, searchDebounceMs]);
    
    /**
     * Filtra per gruppo muscolare
     */
    const filterByGroup = useCallback((group) => {
        setMuscleGroup(group);
        loadInitial(searchTerm, group);
    }, [loadInitial, searchTerm]);
    
    /**
     * Reset e ricarica
     */
    const reset = useCallback(() => {
        cacheRef.current.clear();
        loadInitial('', null);
    }, [loadInitial]);
    
    /**
     * Caricamento iniziale - DISABILITATO: carica solo quando necessario
     */
    // useEffect(() => {
    //     loadInitial('', null);
    // }, []); // Solo al mount - COMMENTATO per non caricare all'inizio
    
    /**
     * Cleanup debounce
     */
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);
    
    return {
        // Dati
        exercises,
        loading,
        initialLoading,
        error,
        
        // Paginazione
        currentPage,
        hasMore,
        total,
        totalPages,
        
        // Filtri
        searchTerm,
        muscleGroup,
        
        // Funzioni
        loadMore,
        search,
        filterByGroup,
        reset,
        refresh: (options = {}) => loadInitial(searchTerm, muscleGroup, options)
    };
};

export default usePaginatedExercises;

